import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { map } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { FeatureSpecSelectors } from '@recruitee/feature-spec-data';
import { I18nService } from '@recruitee/i18n';
import { BillingPlanTier } from '@recruitee/products-types';
import { DialogService, DialogWidth } from '@recruitee/user-interface';

import { FeatureGuardDialogComponent } from './feature-guard-dialog/feature-guard-dialog.component';
import {
  FeatureGuardMode,
  GuardedFeature,
  legacyFeatureToCode,
  UpsellBadgeConfig,
} from './feature-guard.types';

@Injectable({ providedIn: 'root' })
export class FeatureGuardFacade {
  constructor(
    private dialogService: DialogService,
    private router: Router,
    private store: Store,
    private i18n: I18nService,
  ) {}

  public showFeatureInfo(mode: FeatureGuardMode, feature: GuardedFeature) {
    switch (mode) {
      case FeatureGuardMode.Dialog:
        this.showDialog(feature);
        break;
      case FeatureGuardMode.Redirect:
        this.redirectToEmptyState(feature);
        break;
      default:
    }
  }

  public showDialog(feature: GuardedFeature) {
    this.dialogService.openCustom(
      FeatureGuardDialogComponent,
      {
        width: DialogWidth.Small,
      },
      { feature },
    );
  }

  public redirectToEmptyState(feature: GuardedFeature) {
    switch (feature) {
      case GuardedFeature.SendLater:
        this.router.navigate([`mailbox/upgrade`]);
        break;
      default:
    }
  }

  public checkFeatureAvailability(feature: GuardedFeature): Observable<boolean> {
    const featureCode = this.getFeatureCodeFromGuard(feature);

    return this.store.select(FeatureSpecSelectors.forCode(featureCode)).pipe(
      filterBoolean,
      map(entity => entity.entitled),
    );
  }

  public getFeatureCodeFromGuard(feature: GuardedFeature) {
    const featureCode = legacyFeatureToCode[feature];

    if (!featureCode) {
      throw new Error(`FeatureCode ${feature} not found`);
    }

    return featureCode;
  }

  public getPlanWhereFeatureIsAvailable(
    feature: GuardedFeature,
    checkWithPlan: BillingPlanTier | false = false,
  ): Observable<BillingPlanTier | null> {
    const featureCode = this.getFeatureCodeFromGuard(feature);

    if (checkWithPlan) {
      throw new Error(`checkWithPlan is not supported in new config`);
    }
    return this.store.select(FeatureSpecSelectors.forCode(featureCode)).pipe(
      filterBoolean,
      take(1), // TODO - remove take(1) when consumers start utilizing reactivity
      map(entity => entity.availableFrom),
    );
  }

  public getFeaturePlanAvailabilityMap$() {
    return this.store.select(FeatureSpecSelectors.asMap).pipe(filterBoolean);
  }

  public getUpsellDefs(): Partial<Record<BillingPlanTier, UpsellBadgeConfig>> {
    return {
      [BillingPlanTier.Scale]: {
        color: 'scale-reverse',
        label: this.i18n.translate('app.pricing.scale'),
        tooltip: this.i18n.translate('app.pricing.feature_guard.feature_available_scale'),
      },
      [BillingPlanTier.Optimize]: {
        color: 'optimize',
        label: this.i18n.translate('app.pricing.optimize'),
        tooltip: this.i18n.translate('app.pricing.feature_guard.feature_available_optimize'),
      },
      [BillingPlanTier.Grow]: {
        color: 'grow',
        label: this.i18n.translate('app.pricing.grow'),
        tooltip: this.i18n.translate('app.pricing.feature_guard.feature_available_grow'),
      },
    };
  }
}
